import { useMemo } from "react";

import useUsers from "@/hooks/useUsers";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import useCurrentEnvironment from "@/hooks/use-environment";
import {
  bareSupportFormURL,
  buildSupportFormURL,
  pageDetailLines,
} from "@/support/supportFormURL";

/**
 * This hook generates a URL to Freshpaint's Zendesk support form, along with
 * pre-filled form fields containing helpful debugging information.
 *
 * The goal of this hook is to make it easier for our support teams to quickly
 * identify the proper account and environment when a user submits a support
 * request, and to include a bit of context about the user's browser and the
 * page they were on when they decided to reach out for help.
 */
export default function useSupportFormURL(): URL {
  const { loading: userLoading, currentUser } = useUsers();
  const accountMetadata = useAccountMetadata();
  const currentEnvironment = useCurrentEnvironment();

  return useMemo(() => {
    if (
      userLoading ||
      !currentUser ||
      !accountMetadata ||
      !accountMetadata.loaded
    ) {
      return bareSupportFormURL();
    }

    const accountDetails = [
      `<strong>Account:</strong> ${accountMetadata?.companyDetails?.name || "(unnamed account)"} (${accountMetadata?.accountID || "(unknown)"})`,
      `<strong>Project:</strong> ${currentEnvironment?.project?.name || "(unknown)"} (${currentEnvironment?.project.ID})`,
      `<strong>Environment:</strong> ${currentEnvironment?.name || "(unknown)"} (${currentEnvironment?.ID || "(unknown)"})`,
      `<strong>User:</strong> ${currentUser.email} (${currentUser.uid})`,
    ];

    return buildSupportFormURL({
      email: currentUser.email || "",
      sections: [
        { heading: "Account Details", lines: accountDetails },
        { heading: "Page Details", lines: pageDetailLines() },
      ],
    });
  }, [userLoading, currentUser, accountMetadata, currentEnvironment]);
}
